import axios from 'axios';
import router from '@/router';
import { isMobile } from '@/utils/deviceDetection';

// 기본 설정
const commonConfig = {
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
};

// 운영 환경 baseURL 설정
// 1. VUE_APP_PRODUCTION_BASE_URL 환경 변수가 있으면 그 값을 사용 (프로덕션 빌드 시 CI/CD에서 주입)
// 2. 환경 변수가 없고 NODE_ENV가 'production'이면 기본값 사용 또는 경고/에러 처리
// 3. 그 외 (로컬 개발 등)에는 사용되지 않음 (local...URL 사용)
let productionBaseURL = 'https://aitra.net'; // 기본 프로덕션 URL (fallback)
if (process.env.NODE_ENV === 'production') {
  if (process.env.VUE_APP_PRODUCTION_BASE_URL) {
    productionBaseURL = process.env.VUE_APP_PRODUCTION_BASE_URL;
    console.log(`프로덕션 환경 API Base URL (환경 변수 사용): ${productionBaseURL}`);
  } else {
    // VUE_APP_PRODUCTION_BASE_URL 환경 변수가 없는 프로덕션 빌드의 경우
    console.warn(`경고: VUE_APP_PRODUCTION_BASE_URL 환경변수가 설정되지 않았습니다. 기본 프로덕션 URL(${productionBaseURL})로 설정됩니다.`);
  }
}

// 로컬 환경 설정
const mobileIp = '10.250.73.64'; // 모바일 테스트를 위한 PC의 내부 IP
const localApiV1BaseURL = isMobile() ? `http://${mobileIp}:8000` : 'http://localhost:8000';
const localApiV2BaseURL = isMobile() ? `http://${mobileIp}:8001` : 'http://localhost:8001';
const localRestApiBaseURL = isMobile() ? `http://${mobileIp}:8080` : 'http://localhost:8080';

// 운영 환경 여부
const isProduction = process.env.NODE_ENV === 'production';

// API v1용 인스턴스
export const apiClientV1 = axios.create({
  ...commonConfig,
  baseURL: isProduction ? productionBaseURL : localApiV1BaseURL,
});

// API v2용 인스턴스
export const apiClientV2 = axios.create({
  ...commonConfig,
  baseURL: isProduction ? productionBaseURL : localApiV2BaseURL,
});

// RestAPI용 인스턴스 (Vue 개발 서버와 포트가 같고 경로만 다를 경우, baseURL을 상대경로로 두거나 Vue proxy 사용 가능)
// 만약 Vue 개발 서버와 FastAPI 서버 포트가 완전히 다르다면 아래와 같이 명시적 baseURL 설정
export const restApiClient = axios.create({
  ...commonConfig,
  baseURL: isProduction ? productionBaseURL : localRestApiBaseURL,
});


restApiClient.interceptors.response.use(
  response => {
    // 성공적인 응답의 경우, response.data만 반환
    console.log(`restApiClient 응답 성공: ${response.config.url}`, response.data);
    return response.data;
  },
  error => {
    console.error(`restApiClient 응답 오류: ${error.config?.url || '알 수 없는 URL'}`, error);
    // 401 에러 발생 시 로그인 페이지로 이동
    if (error.response?.status === 401) {
      alert('로그인이 만료되었습니다. 로그인 페이지로 이동합니다.');
      router.push('/login');
    }
    return Promise.reject(error.response?.data);
  }
);

// apiClientV1에 응답 인터셉터 추가 (필요한 경우)
apiClientV1.interceptors.response.use(
  response => {
    console.log(`apiClientV1 응답 성공: ${response.config.url}`, response.data);
    return response.data;
  },
  error => {
    console.error(`apiClientV1 응답 오류: ${error.config?.url || '알 수 없는 URL'}`, error);
    if (error.response?.status === 401) {
      // ...
    }
    return Promise.reject(error);
  }
);

// apiClientV2에 응답 인터셉터 추가 (필요한 경우)
apiClientV2.interceptors.response.use(
  response => {
    console.log(`apiClientV2 응답 성공: ${response.config.url}`, response.data);
    return response.data;
  },
  error => {
    console.error(`apiClientV2 응답 오류: ${error.config?.url || '알 수 없는 URL'}`, error);
    if (error.response?.status === 401) {
      // ...
    }
    return Promise.reject(error);
  }
);

// 기존 'api' 인스턴스가 주로 어떤 경로 프리픽스를 사용했는지에 따라
// 아래와 같이 기본 인스턴스를 하나 더 제공하거나, 위의 세 가지 중 하나를 'api'로 export 할 수 있습니다.
// 예를 들어, 대부분의 /api/v1/ 또는 /restapi/ 호출에 사용되었다면 해당 인스턴스를 기본으로 제공
export const api = apiClientV1; // 또는 restApiClient 등 프로젝트 상황에 맞게 기본값 지정

// Jitsi Meet 스크립트 URL 등 환경별로 다른 외부 URL 관리 (선택 사항)
export const jaasAppId = process.env.VUE_APP_JAAS_APP_ID; // .env 파일에서 관리
export const jitsiServerURL = '8x8.vc'; // 필요시 환경별로 변경 가능
export const jitsiScriptUrl = jaasAppId ? `https://${jitsiServerURL}/${jaasAppId}/external_api.js` : null;
//export const jitsiScriptUrl = 'https://8x8.vc/vpaas-magic-cookie-eddda6419dec49e2b55da148a41bc29a/external_api.js';

// WebSocket을 위한 Base URL 생성 시에도 수정된 productionBaseURL 사용
let wsBaseURL = isProduction ? productionBaseURL : localApiV1BaseURL; // apiClientV1과 동일한 로직
if (wsBaseURL) {
  if (wsBaseURL.startsWith('https://')) {
    wsBaseURL = wsBaseURL.replace('https://', 'wss://');
  } else if (wsBaseURL.startsWith('http://')) {
    wsBaseURL = wsBaseURL.replace('http://', 'ws://');
  }
}
export const webSocketApiV1BaseURL = wsBaseURL;

// 로깅 추가하여 최종 URL 확인
if (isProduction) {
  console.log(`최종 프로덕션 API Base URL: ${productionBaseURL}`);
  console.log(`최종 프로덕션 WebSocket Base URL: ${webSocketApiV1BaseURL}`);
} else {
  console.log(`로컬 APIv1 Base URL: ${localApiV1BaseURL}`);
  console.log(`로컬 WebSocket Base URL: ${webSocketApiV1BaseURL}`);
}